import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Observable, Subject, of } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ConfirmBoxComponent, DialogData } from 'src/app/components/dialog/confirm-box/confirm-box.component';
import { IContentComponent } from 'src/app/interfaces/content-component.interface';
import { Address } from 'src/app/models/address.model';
import { CompanyInfo } from 'src/app/models/companyinfo.model';
import { Config } from 'src/app/models/config.model';
import { ContactInvitationApprovedResultModel } from 'src/app/models/contact-invitation-approved-result.model';
import { Country } from 'src/app/models/country.model';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { CaseService } from 'src/app/services/case.service';
import { ConfigService } from 'src/app/services/config.service';
import { ContactInvitationService } from 'src/app/services/contact-invitation.service';
import { ContentService } from 'src/app/services/content.service';
import { SpinnerService } from 'src/app/services/spinner.service';
import { phoneValidator } from 'src/app/validators/phone.validator';
import { postalCodeValidator } from 'src/app/validators/postalcode.validator';

@Component({
  selector: 'app-contact-invitation-approve',
  templateUrl: './contact-invitation-approve.component.html',
  styleUrls: ['./contact-invitation-approve.component.scss']
})
export class ContactInvitationApproveComponent implements OnInit, OnDestroy, IContentComponent {

  private componentDestroyed = new Subject();

  pageComponentName: string = 'ContactInvitationApprove';
  data!: Map<string, string>;

  contactInvitationId!: string;
  contactInvitationType!: number;
  caseUid!: string;

  config!: Config;
  approveContactForm!: FormGroup;
  filteredCountries!: Observable<Country[]>;

  contactInvitationApproved!: ContactInvitationApprovedResultModel;

  isInvalid: boolean = false;

  companyInfo: CompanyInfo | null = null;

  constructor(
    private configService: ConfigService,
    private caseService: CaseService,
    private spinnerService: SpinnerService,
    private matDialog: MatDialog,
    private fb: FormBuilder,
    private authenticationService: AuthenticationService,
    private contactInvitationService: ContactInvitationService,
    private router: Router,
    private route: ActivatedRoute,
    private contentService: ContentService) {

    this.route.params
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(
        (params: Params) => {
          this.caseUid = params['caseUid'];
          this.contactInvitationId = params['id'];
          this.contactInvitationType = +params['type'];

          this.spinnerService.show('Henter informasjon...');
          this.contactInvitationService.checkContactInvitationApproveStatus(this.contactInvitationId, this.caseUid);
        }
      );

    this.contactInvitationService.contactInviationApprovalResult
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(
        (value: ContactInvitationApprovedResultModel | null) => {
          if (value === null) {
            this.isInvalid = true;
          }
          else {
            this.contactInvitationApproved = value;
            this.setFormValues(true);
          }
          this.spinnerService.hide();
        }
      );

    this.configService.config
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(
        (c: Config | null) => {
          if (c !== null) {
            this.config = c;
            this.filteredCountries = this.getFilteredCountries();
          }
        }
      );

    this.contentService.content
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(
        (data: Map<string, string>) => {
          if (data !== undefined) {
            this.data = data;
          }
        }
      );

    this.contentService.loadContent('pageText', this.pageComponentName, ['text', 'label']);
  }

  getLabelValue(label: string): string {
    let value: string = '';
    if (this.data) {
      value = this.data.get(label) ?? '';
    }
    return value;
  }

  ngOnInit(): void {
    this.initForm();
  }

  ngOnDestroy(): void {
    this.componentDestroyed.next(null);
    this.componentDestroyed.unsubscribe();
  }

  initForm(): void {

    this.approveContactForm = new FormGroup({
      OrgNumber: new FormControl<string|null>(null),
      RecipientType: new FormControl<number|null>(0, [Validators.required]),
      FirstName: new FormControl<number|null>(null, [Validators.required, Validators.maxLength(100)]),
      LastName: new FormControl<number|null>(null, [Validators.required, Validators.maxLength(100)]),
      Address: new FormControl<number|null>(null, [Validators.required, Validators.maxLength(200)]),
      CountryCodeAlpha2: new FormControl<string|null>('NO'),
      PostalCode: new FormControl<string|null>(null, [postalCodeValidator()]),
      PostalPlace: new FormControl<string|null>({ value: null, disabled: true }),
      Phone: new FormControl<string|null>(null),
      PhoneCountryCode: new FormControl<string|null>('NO'),
      Email: new FormControl<string|null>(null, [Validators.email, Validators.maxLength(100)]),
    });

    this.approveContactForm.controls.Phone.setValidators([Validators.required, phoneValidator(this.approveContactForm.controls.PhoneCountryCode)]);

    this.approveContactForm.controls.RecipientType.valueChanges
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(
        (value: number) => {
          if (value === 0) {
            this.companyInfo = null;
            this.setFormValues(false);
          }
          else {
            this.setCompanyMode();
          }
        }
      )

    this.approveContactForm.controls.PhoneCountryCode.valueChanges
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(
        (value: string) => {
          this.approveContactForm.controls.Phone.updateValueAndValidity(); //check if valid with new country code
        }
      )

    this.approveContactForm.controls.PostalCode.valueChanges
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(
        (value: string) => {
          if (value && value.length === 4) {
            this.caseService.loadPostalPlace(value)
              .pipe(takeUntil(this.componentDestroyed))
              .subscribe(
                (value: string) => {
                  if (value === 'Ugyldig postnummer') {
                    this.approveContactForm.controls.PostalPlace.setValue('');
                  } else {
                    this.approveContactForm.controls.PostalPlace.setValue(value);
                  }
                }
              );
          } else {
            this.approveContactForm.controls.PostalPlace.setValue('');
          }
        }
      );
  }

  setFormValues(updateRecipientType: boolean): void {
    if (this.approveContactForm) {

      if (updateRecipientType) {
        this.approveContactForm.controls.RecipientType.setValue(this.contactInvitationApproved.RecipientType, { emitEvent: false });
      }

      if (this.approveContactForm.controls.RecipientType.value === 0) {

        this.approveContactForm.controls.FirstName.setValue(this.contactInvitationApproved.FirstName);
        this.approveContactForm.controls.FirstName.disable();
        this.approveContactForm.controls.LastName.setValue(this.contactInvitationApproved.LastName);
        this.approveContactForm.controls.LastName.disable();
      }
      else if (this.approveContactForm.controls.RecipientType.value === 1) {
        this.approveContactForm.controls.FirstName.setValue(null);
        this.approveContactForm.controls.LastName.setValue(this.contactInvitationApproved.FirstName + ' ' + this.contactInvitationApproved.LastName);
        this.approveContactForm.controls.LastName.disable();
      }

      this.approveContactForm.controls.OrgNumber.setValue(null);

      this.approveContactForm.controls.Email.setValue(this.contactInvitationApproved.Email);
      this.approveContactForm.controls.Phone.setValue(this.contactInvitationApproved.Phone);
      this.approveContactForm.controls.PhoneCountryCode.setValue(this.contactInvitationApproved.PhoneCountryCode);

      this.setAddressInfo();

    }
  }
  setAddressInfo(): void {
    if (this.approveContactForm.controls.RecipientType.value === 1 && this.companyInfo !== null) {
      
      var addressValue: Address | null = this.companyInfo.PostalAddress !== null && this.companyInfo.PostalAddress.Address !== null ? this.companyInfo.PostalAddress : this.companyInfo.BusinessAddress;
      if (addressValue !== null) {
        var countryCode: string | null = addressValue.Country_code ? addressValue.Country_code : null;
        var address: string | null = addressValue.Address;
        var postalCode: string | null = addressValue.PostalCode;
        var postalPlace: string | null = addressValue.PostalPlace;

        this.approveContactForm.controls.Address.setValue(address, { emitEvent: false });
        this.approveContactForm.controls.PostalCode.setValue(postalCode, { emitEvent: false });

        this.approveContactForm.controls.PostalPlace.setValue(postalPlace, { emitEvent: false });
        this.approveContactForm.controls.PostalPlace.disable({ emitEvent: false });

        if (countryCode !== null) {
          let country: Country | undefined;
          if (countryCode.length === 2) {
            country = this.config.Countries.find(x => x.cca2 === countryCode);
          } else if (countryCode.length === 3) {
            country = this.config.Countries.find(x => x.cca3 === countryCode);
          }

          if (country !== undefined) {
            this.approveContactForm.controls.CountryCodeAlpha2.setValue(country.cca2, { emitEvent: false });
          }
        }
      }
    }
    else {

      this.approveContactForm.controls.Address.setValue(this.contactInvitationApproved.Address);
      this.approveContactForm.controls.PostalCode.setValue(this.contactInvitationApproved.PostalCode);
      this.approveContactForm.controls.PostalPlace.setValue(this.contactInvitationApproved.PostalPlace);

      if (this.contactInvitationApproved.AddressCountryCode) {
        this.approveContactForm.controls.CountryCodeAlpha2.setValue(this.contactInvitationApproved.AddressCountryCode);
      }
      else {
        this.approveContactForm.controls.CountryCodeAlpha2.setValue('NO');
      }
    }

  }
  setCompanyMode(): void {

      this.approveContactForm.controls.OrgNumber.setValue(null);
      this.approveContactForm.controls.OrgNumber.enable();

      this.approveContactForm.controls.FirstName.setValue(this.companyInfo ? this.companyInfo.Name : null);
      this.approveContactForm.controls.FirstName.enable();

      this.approveContactForm.controls.LastName.setValue(this.contactInvitationApproved.FirstName + ' ' + this.contactInvitationApproved.LastName);
      this.approveContactForm.controls.LastName.disable();

      if (this.companyInfo !== null) {
        this.setAddressInfo();
      }
  }
  companyInfoReceived(event: CompanyInfo | null) {
    this.companyInfo = event;
    this.setCompanyValues();
  }
  setCompanyValues(): void {

    if (this.companyInfo !== null) {
      this.approveContactForm.controls.OrgNumber.disable();

      this.approveContactForm.controls.FirstName.setValue(this.companyInfo?.Name);
      this.approveContactForm.controls.FirstName.disable();
    } 
    else {
      this.approveContactForm.controls.OrgNumber.setValue(null);
      this.approveContactForm.controls.OrgNumber.enable();

      this.approveContactForm.controls.FirstName.setValue(null);
      this.approveContactForm.controls.FirstName.enable();
    }
    
    this.approveContactForm.controls.LastName.setValue(this.contactInvitationApproved.FirstName + ' ' + this.contactInvitationApproved.LastName);
    this.approveContactForm.controls.LastName.disable();

    this.setAddressInfo();

  }

  getFilteredCountries(): Observable<Country[]> {
    //var codes = this.config.Countries.filter(z => z.region.toLowerCase() == 'europe').filter(x => x.callingcodes && x.callingcodes.length > 0)
    var codes = this.config.Countries.filter(x => x.callingcodes && x.callingcodes.length > 0);
    return of(codes);
  }

  getPhoneDisplayValueFromCountryCode(countryCode: string, withName: boolean): string {
    var displayValue: string = '';

    if (this.config && countryCode && countryCode !== '') {
      var country: Country | undefined = this.config.Countries.find(c => c.cca2 === countryCode);
      if (country) {
        if (country.callingcodes && country.callingcodes.length > 0) {
          displayValue = country.callingcodes[0];
        }
        if (withName) {
          displayValue = country.name.common + ' (' + displayValue + ')';
        }
      }
    }

    return displayValue;
  }

  save(): void {

    if (!this.approveContactForm.valid) {
      this.approveContactForm.markAllAsTouched();
      return;
    }

    this.settleContactInvitation(2); //approved
  }

  cancel(): void {
    let initialState: DialogData = {
      body: this.getLabelValue('PopUpConfirm'),
      title: 'Avbryte?',
      okBtnName: 'Ja',
      closeBtnName: 'Nei'
    };

    const dialogRef: MatDialogRef<ConfirmBoxComponent, boolean> = this.matDialog.open(ConfirmBoxComponent, { width: '600px', data: initialState })
    dialogRef
      .afterClosed()
      .subscribe(
        (value: boolean | undefined) => {
          if (value) {
            this.settleContactInvitation(3); //rejected  
          }
        }
      );

  }

  private settleContactInvitation(status: number): void {

    var result: ContactInvitationApprovedResultModel = {
      FirstName: this.approveContactForm.controls.FirstName.value,
      LastName: this.approveContactForm.controls.LastName.value,
      Address: this.approveContactForm.controls.Address.value,
      AddressCountryCode: this.approveContactForm.controls.CountryCodeAlpha2.value,
      PostalPlace: this.approveContactForm.controls.PostalPlace.value,
      PostalCode: this.approveContactForm.controls.PostalCode.value,
      Email: this.approveContactForm.controls.Email.value,
      PhoneCountryCode: this.approveContactForm.controls.PhoneCountryCode.value,
      Phone: this.approveContactForm.controls.Phone.value,
      RecipientType: this.approveContactForm.controls.RecipientType.value,
      Status: status
    };

    this.contactInvitationService.settleContactInvitation(result, this.contactInvitationId, this.caseUid)
      .subscribe(
        () => {
          if (result.Status === 2) {
            //approved
            this.router.navigate(['cases', this.caseUid])
          }
          else if (result.Status === 3) {
            //rejected
            this.router.navigate(['cases']);
          }
        },
        (error: HttpErrorResponse) => {
          console.log(error);
        },
        () => {
          console.log('Finished settle contact invitation with status ' + result.Status);
        }
      )
  }

  logout(): void {
    this.authenticationService.logout();
  }

}
